/**
 * Logger Utility
 * Provides multi-level logging with console and file output (mock)
 */

const LOG_LEVELS = {
  INFO: 'INFO',
  DEBUG: 'DEBUG',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
};

class Logger {
  constructor(moduleName = 'App') {
    this.moduleName = moduleName;
    this.logs = [];
  }

  /**
   * Format log message
   */
  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      module: this.moduleName,
      message,
      data: data || null,
    };

    if (level === LOG_LEVELS.ERROR && data?.error) {
      logEntry.stack = data.error.stack || null;
    }

    return logEntry;
  }

  /**
   * Write to console
   */
  writeToConsole(logEntry) {
    const { timestamp, level, module, message, data, stack } = logEntry;
    const prefix = `[${timestamp}] [${level}] [${module}]`;
    
    switch (level) {
      case LOG_LEVELS.ERROR:
        console.error(prefix, message, data || '', stack || '');
        break;
      case LOG_LEVELS.WARNING:
        console.warn(prefix, message, data || '');
        break;
      case LOG_LEVELS.DEBUG:
        console.debug(prefix, message, data || '');
        break;
      default:
        console.log(prefix, message, data || '');
    }
  }

  /**
   * Write to file (mock implementation)
   */
  async writeToFile(logEntry) {
    // In a real implementation, this would write to logs/ folder
    // For now, we'll store in memory
    this.logs.push(logEntry);
    
    // Limit log history to prevent memory issues
    if (this.logs.length > 1000) {
      this.logs.shift();
    }
  }

  /**
   * Log INFO level
   */
  info(message, data = null) {
    const logEntry = this.formatMessage(LOG_LEVELS.INFO, message, data);
    this.writeToConsole(logEntry);
    this.writeToFile(logEntry);
    return logEntry;
  }

  /**
   * Log DEBUG level
   */
  debug(message, data = null) {
    const logEntry = this.formatMessage(LOG_LEVELS.DEBUG, message, data);
    this.writeToConsole(logEntry);
    this.writeToFile(logEntry);
    return logEntry;
  }

  /**
   * Log WARNING level
   */
  warning(message, data = null) {
    const logEntry = this.formatMessage(LOG_LEVELS.WARNING, message, data);
    this.writeToConsole(logEntry);
    this.writeToFile(logEntry);
    return logEntry;
  }

  /**
   * Log ERROR level
   */
  error(message, error = null, data = null) {
    const logEntry = this.formatMessage(LOG_LEVELS.ERROR, message, {
      error,
      ...data,
    });
    this.writeToConsole(logEntry);
    this.writeToFile(logEntry);
    return logEntry;
  }

  /**
   * Get all logs
   */
  getLogs() {
    return [...this.logs];
  }

  /**
   * Clear logs
   */
  clearLogs() {
    this.logs = [];
  }
}

// Create default logger instance
const defaultLogger = new Logger('App');

// Export both class and default instance
export default defaultLogger;
export { Logger, LOG_LEVELS };

